import { useCallback, useEffect, useRef, useState } from 'react';

import usePlannerFetch from 'hooks/usePlannerFetch';

import {
    ATTACH_PRESCRIPTION_FAILED,
    ATTACH_PRESCRIPTION_MESSAGE_TYPE,
    CREATE_SESSION_FAILED,
    DETACH_PRESCRIPTION_MESSAGE_TYPE,
    ERROR_MESSAGE_TYPE,
    FLIGHT_PLANNER_BASE_URL,
    INVALID_PRESCRIPTION_CODE,
    PLAN_RESULT_MESSAGE_TYPE,
    PRESCRIPTION_ATTACHED_MESSAGE_TYPE,
    PRESCRIPTION_DETACHED_MESSAGE_TYPE,
    PRESCRIPTION_TOO_LARGE_CODE,
    RECONNECT_BACKOFF_MS,
    REPLAN_MESSAGE_TYPE,
    SESSIONS_API,
    WEBSOCKET_CONNECTION_LOST,
    WS_PATH_SUFFIX
} from './constants';
import type {
    ApiRateBounds,
    AttachPrescription,
    AttachPrescriptionMessage,
    CreateSession,
    CreateSessionResponse,
    DetachPrescription,
    DetachPrescriptionMessage,
    PlanResult,
    ReplanMessage,
    ResolvedRoutePlanConfig,
    RoutePlanConfig,
    ServerMessage,
    UpdateConfig,
    UsePlanSessionReturn
} from './types';

const buildWebSocketUrl = ({ sessionId }: { sessionId: string }): string => {
    const url = new URL(`${SESSIONS_API}/${sessionId}${WS_PATH_SUFFIX}`, FLIGHT_PLANNER_BASE_URL);

    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

    return url.toString();
};

const usePlanSession = (): UsePlanSessionReturn => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [planResult, setPlanResult] = useState<PlanResult | null>(null);
    const [config, setConfig] = useState<ResolvedRoutePlanConfig | null>(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isReplanning, setIsReplanning] = useState(false);
    const [replanTimeMs, setReplanTimeMs] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [rateBounds, setRateBounds] = useState<ApiRateBounds | null>(null);
    const [zoneCount, setZoneCount] = useState<number | null>(null);
    const [isAttachingPrescription, setIsAttachingPrescription] = useState(false);
    const [isDetachingPrescription, setIsDetachingPrescription] = useState(false);
    const [attachError, setAttachError] = useState<string | null>(null);

    const webSocketRef = useRef<WebSocket | null>(null);
    const pendingConfigsRef = useRef<RoutePlanConfig[]>([]);
    const pendingPrescriptionsRef = useRef<AttachPrescriptionMessage[]>([]);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isUnmountingRef = useRef(false);
    const currentSessionIdRef = useRef<string | null>(null);

    const { plannerDelete, plannerPost } = usePlannerFetch();

    const handleServerMessage = useCallback((message: ServerMessage) => {
        if (message.type === PLAN_RESULT_MESSAGE_TYPE) {
            setPlanResult(message.result);
            setConfig(message.config);
            setReplanTimeMs(message.replanTimeMs);
            setIsReplanning(false);

            return;
        }

        if (message.type === ERROR_MESSAGE_TYPE) {
            // Route prescription-specific error codes to attachError so the
            // prescription UI can surface them independently of the general
            // replan error state.
            if (
                message.code === INVALID_PRESCRIPTION_CODE ||
                message.code === PRESCRIPTION_TOO_LARGE_CODE
            ) {
                setAttachError(message.message);
                setIsAttachingPrescription(false);
                setIsDetachingPrescription(false);

                return;
            }

            setError(message.message);
            setIsReplanning(false);

            return;
        }

        if (message.type === PRESCRIPTION_ATTACHED_MESSAGE_TYPE) {
            setRateBounds(message.rateBounds);
            setZoneCount(message.zoneCount);
            setIsAttachingPrescription(false);
            setAttachError(null);

            return;
        }

        if (message.type === PRESCRIPTION_DETACHED_MESSAGE_TYPE) {
            setRateBounds(null);
            setZoneCount(null);
            setIsDetachingPrescription(false);
            setAttachError(null);
        }
    }, []);

    const flushPendingConfigs = useCallback((webSocket: WebSocket) => {
        const pending = pendingConfigsRef.current;

        if (pending.length === 0) {
            return;
        }

        pendingConfigsRef.current = [];

        for (const pendingConfig of pending) {
            const message: ReplanMessage = {
                config: pendingConfig,
                type: REPLAN_MESSAGE_TYPE
            };

            webSocket.send(JSON.stringify(message));
        }
    }, []);

    const flushPendingPrescriptions = useCallback((webSocket: WebSocket) => {
        const pending = pendingPrescriptionsRef.current;

        if (pending.length === 0) {
            return;
        }

        pendingPrescriptionsRef.current = [];

        // Only the most recent attachPrescription is meaningful — a queued
        // sequence of attachments collapses to the last one (cancel + restart
        // semantics from the plan).
        const latest = pending[pending.length - 1];

        webSocket.send(JSON.stringify(latest));
    }, []);

    const connectWebSocket = useCallback(
        (targetSessionId: string, isInitialConnection: boolean) => {
            const url = buildWebSocketUrl({ sessionId: targetSessionId });
            const webSocket = new WebSocket(url);
            let didOpen = false;

            webSocket.onopen = () => {
                didOpen = true;
                reconnectAttemptsRef.current = 0;

                if (isInitialConnection) {
                    setIsConnecting(false);
                }

                flushPendingConfigs(webSocket);
                flushPendingPrescriptions(webSocket);
            };

            webSocket.onmessage = messageEvent => {
                try {
                    const parsed = JSON.parse(messageEvent.data as string) as ServerMessage;

                    handleServerMessage(parsed);
                } catch (parseError) {
                    console.error(parseError);
                }
            };

            webSocket.onerror = errorEvent => {
                console.error(errorEvent);

                if (isInitialConnection && !didOpen) {
                    setIsConnecting(false);
                    setError(WEBSOCKET_CONNECTION_LOST);
                }
            };

            webSocket.onclose = () => {
                if (isUnmountingRef.current) {
                    return;
                }

                if (currentSessionIdRef.current !== targetSessionId) {
                    return;
                }

                if (isInitialConnection && !didOpen) {
                    setIsConnecting(false);
                    setError(WEBSOCKET_CONNECTION_LOST);

                    return;
                }

                const attempt = reconnectAttemptsRef.current;

                if (attempt >= RECONNECT_BACKOFF_MS.length) {
                    setError(WEBSOCKET_CONNECTION_LOST);
                    setIsReplanning(false);

                    return;
                }

                const delay = RECONNECT_BACKOFF_MS[attempt];

                reconnectAttemptsRef.current = attempt + 1;

                reconnectTimeoutRef.current = setTimeout(() => {
                    connectWebSocket(targetSessionId, false);
                }, delay);
            };

            webSocketRef.current = webSocket;
        },
        [flushPendingConfigs, flushPendingPrescriptions, handleServerMessage]
    );

    const createSession: CreateSession = useCallback(
        async params => {
            setIsConnecting(true);
            setError(null);

            const { data, error: responseError } = await plannerPost<CreateSessionResponse>(
                SESSIONS_API,
                JSON.stringify(params)
            );

            if (responseError || !data) {
                setError(responseError ?? CREATE_SESSION_FAILED);
                setIsConnecting(false);

                return null;
            }

            currentSessionIdRef.current = data.sessionId;
            reconnectAttemptsRef.current = 0;
            setSessionId(data.sessionId);
            setPlanResult(data.initialResult);
            setConfig(data.config);

            connectWebSocket(data.sessionId, true);

            return { sessionId: data.sessionId };
        },
        [connectWebSocket, plannerPost]
    );

    const updateConfig: UpdateConfig = useCallback(({ config: partialConfig }) => {
        const webSocket = webSocketRef.current;

        setIsReplanning(true);

        if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
            pendingConfigsRef.current.push(partialConfig);

            return;
        }

        const message: ReplanMessage = {
            config: partialConfig,
            type: REPLAN_MESSAGE_TYPE
        };

        webSocket.send(JSON.stringify(message));
    }, []);

    const attachPrescription: AttachPrescription = useCallback(({ gapPolicy, rateUnit, zones }) => {
        const message: AttachPrescriptionMessage = {
            gapPolicy: gapPolicy ?? null,
            rateUnit,
            type: ATTACH_PRESCRIPTION_MESSAGE_TYPE,
            zones
        };

        setAttachError(null);
        setIsAttachingPrescription(true);

        const webSocket = webSocketRef.current;

        if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
            pendingPrescriptionsRef.current.push(message);

            return;
        }

        try {
            webSocket.send(JSON.stringify(message));
        } catch (sendError) {
            console.error(sendError);
            setIsAttachingPrescription(false);
            setAttachError(ATTACH_PRESCRIPTION_FAILED);
        }
    }, []);

    const detachPrescription: DetachPrescription = useCallback(() => {
        const webSocket = webSocketRef.current;

        // Dropping any queued attach before it reaches the wire — the user's
        // intent is "no prescription", so a pending attach is obsolete.
        pendingPrescriptionsRef.current = [];

        if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
            setRateBounds(null);
            setZoneCount(null);
            setIsAttachingPrescription(false);
            setAttachError(null);

            return;
        }

        const message: DetachPrescriptionMessage = {
            type: DETACH_PRESCRIPTION_MESSAGE_TYPE
        };

        setIsDetachingPrescription(true);

        try {
            webSocket.send(JSON.stringify(message));
        } catch (sendError) {
            console.error(sendError);
            setIsDetachingPrescription(false);
            setAttachError(ATTACH_PRESCRIPTION_FAILED);
        }
    }, []);

    const disconnect = useCallback(() => {
        const targetSessionId = currentSessionIdRef.current;

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (webSocketRef.current) {
            webSocketRef.current.close();
            webSocketRef.current = null;
        }

        currentSessionIdRef.current = null;
        pendingConfigsRef.current = [];
        pendingPrescriptionsRef.current = [];
        reconnectAttemptsRef.current = 0;

        if (targetSessionId) {
            plannerDelete(`${SESSIONS_API}/${targetSessionId}`);
        }

        setSessionId(null);
        setPlanResult(null);
        setConfig(null);
        setReplanTimeMs(null);
        setIsReplanning(false);
        setRateBounds(null);
        setZoneCount(null);
        setIsAttachingPrescription(false);
        setIsDetachingPrescription(false);
        setAttachError(null);
    }, [plannerDelete]);

    useEffect(() => {
        isUnmountingRef.current = false;

        return () => {
            isUnmountingRef.current = true;

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }

            if (webSocketRef.current) {
                webSocketRef.current.close();
            }

            const targetSessionId = currentSessionIdRef.current;

            if (targetSessionId) {
                plannerDelete(`${SESSIONS_API}/${targetSessionId}`);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        attachError,
        attachPrescription,
        config,
        createSession,
        detachPrescription,
        disconnect,
        error,
        isAttachingPrescription,
        isConnecting,
        isDetachingPrescription,
        isReplanning,
        planResult,
        rateBounds,
        replanTimeMs,
        sessionId,
        updateConfig,
        zoneCount
    };
};

export default usePlanSession;
